import { Registration } from '@stax-core/types-omni';

const representativesMapper = (registration: Registration) => {
  const reps = registration.meta.representatives || [];

  return reps.reduce((acc, rep, index): object => {
    const pos = ++index;
    return {
      ...acc,
      [`owner${pos}_name`]: `${rep.first_name} ${rep.last_name}`,
      [`owner${pos}_pct`]: rep.ownership_percentage,
      [`owner${pos}_title`]: rep.title,
      [`owner${pos}_phone`]: rep.phone,
      [`owner${pos}_dob`]: rep.date_of_birth,
      [`owner${pos}_ssn`]: rep.ssn,
      [`owner${pos}_address`]: rep.address_1,
      [`owner${pos}_citystatezip`]: `${rep.address_city}, ${rep.address_state}, ${rep.address_zip}`,
    };
  }, {});
};

export const commonFields = (
  registration: Registration,
): { [key: string]: any } => {
  const companyType = registration.company_type;

  let type_association = ''; // Association/Estate/Trust
  let type_govt = ''; // Government (Federal/State/Local)
  let type_soleprop = ''; // Individual/Sole Proprietor
  let type_financial = ''; // Financial Institution
  let type_llc = '';
  let type_nonprofit = ''; // Tax-Exempt Organization (501C)
  let type_partnership = '';
  let type_privatecorp = '';
  let type_public = '';
  let type_sec = ''; // SEC-Registered Entity

  // determine which 'company_type' is assigned to the registration
  // to fill the appropriate HelloSign checkbox with an 'x' instead of an empty string ''

  switch (companyType) {
    case 'A':
      type_association = 'x';
      break;
    case 'G':
      type_govt = 'x';
      break;
    case 'S':
      type_soleprop = 'x';
      break;
    case 'F':
      type_financial = 'x';
      break;
    case 'L':
      type_llc = 'x';
      break;
    case 'E':
      type_nonprofit = 'x';
      break;
    case 'P':
      type_partnership = 'x';
      break;
    case 'V':
      type_privatecorp = 'x';
      break;
    case 'B':
      type_public = 'x';
      break;
    case 'R':
      type_sec = 'x';
      break;
  }

  const refundPolicy = registration.refund_policy;

  let refund_no = ''; // no refunds
  let refund_30 = ''; // 30 days or less
  let refund_exchange = ''; // exhange only
  let refund_other = '';

  // determine which 'refund_policy' is assigned to the registration
  // to fill the appropriate HelloSign checkbox with an 'x' instead of an empty string ''

  switch (refundPolicy) {
    case 'N':
      refund_no = 'x';
      break;
    case 'T':
      refund_30 = 'x';
      break;
    case 'E':
      refund_exchange = 'x';
      break;
    case 'O':
      refund_other = 'x';
      break;
  }

  const reasonForApplying =
    registration.reason_for_applying &&
    registration.reason_for_applying.replace(/ /g, '');

  let app_never = '';
  let app_change = '';

  // determine which 'reason_for_applying' is assigned to the registration
  // to fill the appropriate HelloSign checkbox with an 'x' instead of an empty string ''
  switch (reasonForApplying) {
    case 'ProcessorChange':
      app_change = 'x';
      break;
    case 'NeverAcceptedCardsBefore':
      app_never = 'x';
      break;
  }

  const fields = {
    hellosign_meta_1: registration.business_legal_name || '',
    company: registration.business_dba || '',
    address: `${registration.business_location_address_1 || ''} ${
      registration.business_location_address_2 || ''
    }`.trim(),
    city: registration.business_location_address_city || '',
    state: registration.business_location_address_state || '',
    zip: registration.business_location_address_zip || '',
    website: registration.business_website || '',
    business_phone: registration.business_location_phone_number || '',
    phone: registration.phone_number || '',
    fax: registration.business_fax || '',
    hellosign_meta_6: registration.business_tax_id || '',
    firstname: registration.first_name || '',
    lastname: registration.last_name || '',
    email: registration.email || '',
    jobtitle: registration.title || '',
    hellosign_meta_10: registration.service_you_provide || '',
    hellosign_meta_12: `${registration.owner_address_1 || ''} ${
      registration.owner_address_2 || ''
    }`.trim(),
    hellosign_meta_13: registration.owner_address_city || '',
    hellosign_meta_14: registration.owner_address_state || '',
    hellosign_meta_15: registration.owner_address_zip || '',
    drivers_license_ID: registration.drivers_license_id,
    issuer_state: registration.drivers_license_state,
    hellosign_meta_16: registration.annual_volume || '',
    avg_monthly_volume: (Number(registration.annual_volume || 0) / 12).toFixed(
      2,
    ),
    hellosign_meta_17: registration.avg_trans_size || '',
    hellosign_meta_18: registration.highest_trans_amount || '',
    hellosign_meta_19: registration.card_present_percent || '',
    hellosign_meta_20: registration.card_not_present_percent || '',
    hellosign_meta_21: registration.b2b_percent || '',
    hellosign_meta_22: registration.international || '',
    hellosign_meta_23: registration.business_open_date || '',
    hellosign_meta_24: registration.user_dob || '',
    controlowner_pct: registration.meta.ownership_percentage || '',
    controlowner_ssn: registration.user_ssn,
    ...representativesMapper(registration),
    bank_routing: registration.bank_routing_number,
    bank_account: registration.bank_account_number,
    bank_institution_name: registration.bank_institution_name,
    billing_phone: registration.business_phone_number || '',
    billing_address: `${registration.business_address_1 || ''} ${
      registration.business_address_2 || ''
    }`.trim(),
    billingcity_state_zip: `${registration.business_address_city || ''}, ${
      registration.business_address_state || ''
    } ${registration.business_address_zip || ''}`,
    secondary_bank_account_number: registration.secondary_bank_account_number,
    secondary_bank_routing_number: registration.secondary_bank_routing_number,
    iso_retail: registration.bus_type === '1',
    iso_restaurant: registration.bus_type === '11',
    iso_moto: registration.bus_type === '12',
    iso_internet: registration.bus_type === '18',
    moto_percent: registration.moto_percent ?? '',
    internet_percent: registration.internet ?? '',
    mcc: registration.mcc ?? '',

    // business type
    type_association,
    type_govt,
    type_soleprop,
    type_financial,
    type_llc,
    type_nonprofit,
    type_partnership,
    type_privatecorp,
    type_public,
    type_sec,
    app_never: app_never, // never accepted cards
    app_change: app_change,
    refund_no,
    refund_30,
    refund_exchange,
    refund_other,
    discountrate: registration.plan_dcamnt || '', // discount rate
    peritem: registration.plan_txamnt || '', // per item fee
    amexrate: registration.amex_qual_disc_rate || '', // amex rate
    amexperitem: registration.amex_qual_trans_fee || '',
  };

  if (registration.secondary_bank_account_type) {
    fields[
      `secondary_bank_account_type_${registration.secondary_bank_account_type}`
    ] = 'x';
  }

  if (
    registration.secondary_bank_account_purpose &&
    registration.secondary_bank_account_purpose !== 'trust'
  ) {
    fields[
      `secondary_bank_account_purpose_${registration.secondary_bank_account_purpose}`
    ] = 'x';
  }

  return fields;
};
